import type { Dispatch, SetStateAction } from 'react';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useGuestSiteController } from './GuestSite.controller';
import { AnalyticsContext, useLocation } from '@shared/core';
import { BrannanNavigation, DesignLayoutType, EventPageVisibility, PageKind } from '@graphql/generated-base';
import { ScrollToProvider } from '@apps/guest/packages/layout-engine/layouts/LayoutAloha/providers/ScrollToProvider/ScrollToProvider';
import type { GuestSiteEvent } from '@apps/guest/packages/layout-engine/layouts/layout.types';
import type { StaticContext } from '@react-router';
import { Box, useDisclosure } from '@withjoy/joykit';
import JustFonts from '@apps/guest/packages/layout-engine/components/JustFonts';
import { GuestSiteDebugger } from '@apps/guest/components/GuestSiteDebugger';
import { ActionBar } from '@apps/guest/packages/layout-engine/components/ActionBar';
import { UnlockDialogProvider } from '@apps/guest/packages/layout-engine/components/UnlockDialog/UnlockDialogProvider';
import { GuestIdentificationDialogProvider } from '@apps/guest/packages/layout-engine/components/GuestIdentificationDialog';
import { AutoUnlockGate } from '@apps/guest/packages/layout-engine/components/UnlockDialog/AutoUnlockGate';
import { LayoutProvider } from '@shared/context/LayoutProvider';
import { wrapWithGuestSiteHealthProvider } from './GuestSite.health';
import { GuestSiteFeatureSet } from '@apps/guest/packages/layout-engine/components/GuestSiteFeatureSet/GuestSiteFeatureSet';
import { useNativeState } from '@shared/core/native';
import { useInformParentOnMousedown } from './hooks/useInformParentOnMousedown';
import { EventDesignThemingProvider } from '@shared/core/website/theming';
import { EventDesignJoyKitThemeOverridesProvider } from '@shared/core/website/theming/EventDesignJoyKitThemeOverridesProvider';
import { ColorPaletteProvider } from '@apps/guest/packages/layout-engine/layouts/ColorPaletteProvider/ColorPaletteProvider';
import { SmartAppBanner, SmartAppBannerProvider, useSmartAppBannerProvider } from '@shared/components/SmartAppBanner';
import { usePurchaseContextIdFromLocation } from '@apps/registry/common/utils/dropship';
import { useUpdateSearchParam } from '@shared/utils/updateSearchParam';
import { useIsGuestSiteSsrEnabled } from '@apps/guest/shared/hooks/useIsGuestSiteSsrEnabled';
import { EVENT_PAGE_SLUGS } from '@apps/guest/shared/constants/eventPageSlugs';
import { DiscountsProvider } from '@shared/components/DiscountsProvider/DiscountsProvider';
import { HotelsDiscountsProvider } from '@shared/components/DiscountsProvider/hotels/HotelsDiscountsProvider';
import { UnpublishedWebsite } from './components/UnpublishedWebsite/UnpublishedWebsite';
import { StickyNavBarOffsetProvider } from '@apps/guest/packages/layout-engine/components/StickyNavBarOffset';
import { WebsiteBannerProvider, useWebsiteBannerOffset } from '@shared/components/WebsiteBanner';
import { GUEST_SITE_START_PORTAL_ID } from './GuestSite.constants';
import { GuestSiteHead } from './GuestSiteHead';

import { LazyStatusBanner } from './components/StatusBanner/StatusBanner.lazy';
import { LazyLayoutAloha } from '@apps/guest/packages/layout-engine/layouts/LayoutAloha/LayoutAloha.lazy';
import { LazyLayoutBrannan } from '@apps/guest/packages/layout-engine/layouts/LayoutBrannan/LayoutBrannan.lazy';
import { LazyLayoutBrannanSinglePage } from '@apps/guest/packages/layout-engine/layouts/LayoutBrannan/LayoutBrannanSinglePage.lazy';
import { LazyPreviewListener } from './PreviewListener.lazy';
import { TemplatePreviewProvider } from './TemplatePreviewContext';
import { AccommodationsLayoutOverrideProvider } from './AccommodationsLayoutOverrideContext';
import { PhotoGalleryLayoutOverrideProvider } from './PhotoGalleryLayoutOverrideContext';
import { LazyAnnouncement } from '@apps/guest/packages/layout-engine/components/Announcement/Announcement.lazy';
import { LazyPurchaseConfirmationDialog } from '@apps/guest/packages/layout-engine/widgets/Custom/components/Accommodations/components/PurchaseConfirmationDialog/PurchaseConfirmationDialog.lazy';
import { RichTextStylesProvider } from '@apps/guest/packages/layout-engine/widgets/Custom/components/RichText/RichTextStylesProvider';
import { usePreloadTranslationNamespaces } from '@shared/hooks/usePreloadTranslationNamespaces';
import { useWelcomePage } from '@shared/hooks/useWelcomePage';
import { useCurrentPageSlug } from '@shared/utils/hooks/useCurrentPageSlug';
import { LazyWelcomePagePasswordGate } from '@apps/guest/packages/layout-engine/components/WelcomePagePasswordGate/WelcomePagePasswordGate.lazy';
import { LazyWelcomePageOtpGate } from '@apps/guest/packages/layout-engine/components/WelcomePageOtpGate/WelcomePageOtpGate.lazy';
import { PageContentVisibilityProvider } from '@apps/guest/packages/layout-engine/components/PageContentVisibilityProvider';
import { usePrefetchSsrQueries } from './hooks/usePrefetchSsrQueries';
import { useIsStaysRedesignActive } from '@apps/guest/packages/layout-engine/widgets/Custom/components/Honeymoon/useIsStaysRedesignActive';
import { useFetchCustomPageData } from '@apps/guest/packages/layout-engine/widgets/Custom/useFetchCustomPageData';
import { useFetchAccommodationsSsrData } from '@apps/guest/packages/layout-engine/widgets/Custom/useFetchAccommodationsSsrData';
import { computeShouldRenderOtpGate } from './gateDecision';

// Layout-level prefetches; runs above the `event && layout` gate so queries fire concurrently with EventWebsiteGuest.
const PrefetchSsrQueriesRenderer: React.FC<{ eventHandle: string }> = ({ eventHandle }) => {
  usePrefetchSsrQueries({ eventHandle });
  return null;
};

// Conditional mount keeps the GuestEventHotelRoomBlocks observer off non-redesign events —
// controller's `refetchQueries({ include: refetchPageQueries.accommodations })` matches by
// operation name and would bypass the hook's internal `skip`.
const HoneymoonAccommodationsPrefetchInner: React.FC<{ eventHandle: string; eventId: string | undefined }> = ({ eventHandle, eventId }) => {
  const { data: pageContainerData } = useFetchCustomPageData(eventHandle, false);
  useFetchAccommodationsSsrData({ eventId, pageContainerData });
  return null;
};

const HoneymoonAccommodationsPrefetchRenderer: React.FC<{ eventHandle: string; eventId: string | undefined }> = ({ eventHandle, eventId }) => {
  const isStaysRedesignActive = useIsStaysRedesignActive();
  if (!isStaysRedesignActive) return null;
  return (
    <HoneymoonAccommodationsPrefetchInner
      eventHandle={eventHandle}
      eventId={eventId}
    />
  );
};

export interface GuestSiteLayoutProps
  extends Readonly<{
    eventHandle: string;
    event: GuestSiteEvent;
    layout: DesignLayoutType;
    setLoadingTheme?: Maybe<Dispatch<SetStateAction<boolean>>>;
    setIsLastPageVisible?: (value: boolean) => void;
    setIsPastFirstPage?: (value: boolean) => void;
    isNativeAccommodationPage: boolean;
    isNativeBrannanOverridePage: boolean;
    hideNav: boolean;
  }> {}

const GuestSiteLayout: React.FC<GuestSiteLayoutProps> = ({
  eventHandle,
  event,
  layout,
  setLoadingTheme,
  setIsLastPageVisible,
  setIsPastFirstPage,
  isNativeBrannanOverridePage,
  hideNav,
}) => {
  const brannanNavigation = event.eventDesign?.brannanNavigation;
  const layoutSwitch = () => {
    const layoutTypes = {
      [DesignLayoutType.aloha]: LazyLayoutAloha,
      [DesignLayoutType.brannan]: !isNativeBrannanOverridePage && brannanNavigation === BrannanNavigation.singlePage ? LazyLayoutBrannanSinglePage : LazyLayoutBrannan,
    };
    const Layout = layoutTypes[layout];

    return (
      (event && (
        <Layout
          setLoadingTheme={setLoadingTheme}
          setIsLastPageVisible={setIsLastPageVisible}
          setIsPastFirstPage={setIsPastFirstPage}
          eventHandle={eventHandle}
          event={event}
          hideNav={hideNav}
        />
      )) ||
      null
    );
  };

  return layoutSwitch();
};

export interface GuestSiteBodyProps
  extends Readonly<{
    eventHandle: string;
    staticContext: StaticContext | undefined;
  }> {}

const GuestSiteBodyBase: React.FC<GuestSiteBodyProps> = wrapWithGuestSiteHealthProvider(({ eventHandle, staticContext }) => {
  const {
    event,
    layout,
    eventPageRoutes,
    isPreviewing,
    handleOnPreviewUpdate,
    handleOnRefetchData,
    justFonts,
    graphicAccent,
    shouldRenderActionBar,
    setLoadingTheme,
    setIsPastFirstPage,
    showAnnouncement,
    onAnnouncementDismiss,
    isNativeAccommodationPage,
    isNativeBrannanOverridePage,
    showUnpublishedPage,
    suppressGuestSiteUntilAuth,
    loadingGuestSite,
    isLoadingWebsitePublishCapability,
  } = useGuestSiteController({
    eventHandle,
    staticContext,
  });
  const { isNative } = useNativeState();
  const isFullySsrEnabled = useIsGuestSiteSsrEnabled();
  const { checkIsWelcomePage, loading: isWelcomePageLoading } = useWelcomePage();
  const { currentPageSlug, isCustomPageSlug } = useCurrentPageSlug(eventHandle);

  usePreloadTranslationNamespaces(
    'joykit',
    'guestKit',
    'layoutEngine',
    'sharedComponents',
    'countries',
    'guestSiteCustom',
    'travelMap',
    'accommodationsGuest',
    'hotelTile',
    'guestSiteTravel',
    'dates',
    'accommodationsConsole',
    'websitePages',
    'publishReminder',
    'eventSiteApps',
    'guestRegistry',
    'guestUnlock',
    'inlineEditor',
  );

  const { smartAppBannerHeight } = useSmartAppBannerProvider();

  const { pathname } = useLocation();
  const purchaseId = usePurchaseContextIdFromLocation();
  const updateSearchParam = useUpdateSearchParam();
  const analytics = useContext(AnalyticsContext);
  const [isPurchaseConfirmationOpen, setIsPurchaseConfirmationOpen] = useState(false);
  const { isOpen: isPurchaseConfirmationDialogOpen, onClose: onPurchaseConfirmationClose } = useDisclosure({
    isOpen: isPurchaseConfirmationOpen,
    onClose: () => {
      updateSearchParam('pcId', null);
      setIsPurchaseConfirmationOpen(false);
    },
  });
  // Get website banner offset
  const { bannerOffset } = useWebsiteBannerOffset();

  const isTravelOrAccomPage = useMemo(() => pathname.includes('/travel') || pathname.includes('/accommodation'), [pathname]);
  const shouldRenderPurchaseConfirmationDialog = !!purchaseId && isTravelOrAccomPage;

  useEffect(() => {
    if (shouldRenderPurchaseConfirmationDialog) {
      setIsPurchaseConfirmationOpen(true);
      analytics.track({
        category: 'guest.purchase',
        action: 'PageViewed',
        extraInfo: { name: 'registryPurchaseConfirmation', pageType: pathname.includes('/travel') ? 'travel' : 'accommodation' },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldRenderPurchaseConfirmationDialog]);

  // hook to inform irame parent on mousedown
  useInformParentOnMousedown(isPreviewing);

  // Use website banner offset (no need for useBannersOffset anymore)
  const totalOffset = Math.max(smartAppBannerHeight, bannerOffset);

  const hasUnlocked = event?.activeSession?.hasUnlocked ?? false;
  const isRootRoute = !currentPageSlug;
  const welcomePage = useMemo(() => {
    const welcomePages = event?.pages?.filter(page => checkIsWelcomePage(page.pageSlug, page.type).isWelcomePage);

    if (!welcomePages?.length) {
      return undefined;
    }

    if (isRootRoute) {
      return welcomePages[0];
    }

    if (isCustomPageSlug) {
      // CORE-6470: prefer the custom (non-legacy) welcome row when the URL targets /page/<slug>.
      return welcomePages.find(page => page.pageKind !== PageKind.systemLegacy) ?? welcomePages[0];
    }

    return welcomePages.find(page => page.pageKind === PageKind.systemLegacy) ?? welcomePages[0];
  }, [checkIsWelcomePage, event?.pages, isCustomPageSlug, isRootRoute]);
  const isWelcomeLocked = !!welcomePage && (welcomePage.visibility === EventPageVisibility.passwordProtected || welcomePage.private);
  // When all pages are password-protected, the backend returns an empty pages
  // array for non-admin users who haven't unlocked. Detect this so we can
  // still show the gate on potential welcome routes.
  const allPagesFilteredByPassword = !hasUnlocked && Array.isArray(event?.pages) && event?.pages.length === 0;
  // Defer gate resolution only on routes that can render the welcome page.
  const isPotentialWelcomeRoute = isRootRoute || currentPageSlug === EVENT_PAGE_SLUGS.Welcome;
  const isWelcomeRouteLoading = !isPreviewing && isPotentialWelcomeRoute && isWelcomePageLoading;
  const isOnWelcomeRoute = useMemo(() => {
    if (!welcomePage || isWelcomeRouteLoading) {
      return false;
    }

    if (isRootRoute) {
      return true;
    }

    if (currentPageSlug !== welcomePage.pageSlug) {
      return false;
    }

    return isCustomPageSlug ? welcomePage.pageKind !== PageKind.systemLegacy : welcomePage.pageKind === PageKind.systemLegacy;
  }, [currentPageSlug, isCustomPageSlug, isRootRoute, isWelcomeRouteLoading, welcomePage]);
  const shouldHideNav = !isPreviewing && (isWelcomeLocked || allPagesFilteredByPassword) && !hasUnlocked;
  const shouldRenderWelcomeGate =
    !isPreviewing && !isWelcomeRouteLoading && !hasUnlocked && ((isWelcomeLocked && isOnWelcomeRoute) || (allPagesFilteredByPassword && isPotentialWelcomeRoute));
  // Pure helper — see gateDecision.ts for the rationale. Site-wide (no
  // welcome-route scope) so deep-link routes don't fall through to the
  // legacy AutoUnlockGate dialog.
  const shouldRenderOtpGate = computeShouldRenderOtpGate({
    isPreviewing,
    hasUnlocked,
    requiresGuestIdentification: event?.settings?.requiresGuestIdentification ?? false,
    allPagesFilteredByPassword,
  });
  const shouldRenderFullPageGate = shouldRenderWelcomeGate || shouldRenderOtpGate;

  // Stacked layouts (Aloha, Brannan single-page) defer non-target chrome so
  // the initial paint focuses on the deep-linked route. Brannan classic
  // short-circuits — renders one page per URL so there's nothing stacked to
  // hide, and a classic nav click otherwise gets trapped behind the initial-
  // slug ref until the fallback timer fires.
  //
  // `isNativeBrannanOverridePage` (e.g. native accommodations / things-to-do
  // on a single-page Brannan event) forces the classic Brannan render even
  // when brannanNavigation === singlePage — mirrors the switch in
  // GuestSiteLayout above — so treat it as "classic" here too.
  //
  // Computed here (before event/layout resolve) so `PageContentVisibilityProvider`
  // can mount at the very top of the return — `PrefetchSsrQueriesRenderer`
  // then fires layout-level queries in parallel with `EventWebsiteGuest`
  // instead of waiting behind the `event && layout` gate. Once `layout`
  // resolves, the provider's effect picks up the new `isInitiallyRevealed`
  // value and flips `allRevealed` accordingly.
  const isInitiallyRevealed = layout === DesignLayoutType.brannan && (isNativeBrannanOverridePage || event?.eventDesign?.brannanNavigation !== BrannanNavigation.singlePage);

  return (
    <PageContentVisibilityProvider
      eventHandle={eventHandle}
      pages={event?.pages}
      isInitiallyRevealed={isInitiallyRevealed}
    >
      <PrefetchSsrQueriesRenderer eventHandle={eventHandle} />
      <HoneymoonAccommodationsPrefetchRenderer
        eventHandle={eventHandle}
        eventId={event?.id}
      />
      {/* Only render GuestSiteHead when full SSR is enabled — when disabled,
          GuestSiteHeadCsr in GuestSite.tsx handles SEO meta tags */}
      {isFullySsrEnabled && <GuestSiteHead event={event} />}
      <Box paddingTop={totalOffset}>
        <LazyStatusBanner />
        <GuestSiteFeatureSet
          loadingGuestSite={loadingGuestSite || isLoadingWebsitePublishCapability || isWelcomeRouteLoading}
          staticContext={staticContext}
        >
          {suppressGuestSiteUntilAuth ? null : event && showUnpublishedPage ? (
            <UnpublishedWebsite />
          ) : event && layout ? (
            <LayoutProvider
              layout={layout}
              brannanNavigation={event.eventDesign?.brannanNavigation}
            >
              <EventDesignThemingProvider eventDesign={event.eventDesign}>
                <UnlockDialogProvider>
                  <AutoUnlockGate
                    hasPages={event.pages.length > 0}
                    hasUnlocked={event.activeSession.hasUnlocked}
                    isPreviewing={isPreviewing}
                    isFullPageGateActive={shouldRenderFullPageGate}
                  />
                  <GuestIdentificationDialogProvider isFullPageGateActive={shouldRenderFullPageGate}>
                    <ColorPaletteProvider eventDesign={event.eventDesign}>
                      <EventDesignJoyKitThemeOverridesProvider>
                        <RichTextStylesProvider>
                          {showAnnouncement && !isNative && (
                            <LazyAnnouncement
                              onAnnouncementClosed={() => onAnnouncementDismiss(event.info.announcementBanner.title)}
                              content={<>{event.info.announcementBanner.message}</>}
                              title={event.info.announcementBanner.title}
                              graphicAccent={graphicAccent}
                            />
                          )}
                          {justFonts ? (
                            <JustFonts />
                          ) : shouldRenderOtpGate ? (
                            // Site-wide gate for events requiring guest identification.
                            // Renders on every route while the BE is filtering pages,
                            // so deep links don't bypass the gate via AutoUnlockGate's
                            // fallback legacy dialog. The BE derives `hasUnlocked`
                            // from `hasIdentity` for these events, so the existing
                            // empty-pages signal still triggers gate-mode here.
                            <LazyWelcomePageOtpGate
                              eventId={event.id}
                              eventDisplayName={event.info.eventDisplayName ?? ''}
                            />
                          ) : shouldRenderWelcomeGate ? (
                            <LazyWelcomePagePasswordGate
                              eventId={event.id}
                              eventDisplayName={event.info.eventDisplayName ?? ''}
                            />
                          ) : (
                            <ScrollToProvider>
                              <AccommodationsLayoutOverrideProvider>
                                <PhotoGalleryLayoutOverrideProvider>
                                  {isPreviewing && (
                                    <LazyPreviewListener
                                      eventHandle={eventHandle}
                                      eventPageRoutes={eventPageRoutes}
                                      eventDesign={event?.eventDesign}
                                      onRefetchData={handleOnRefetchData}
                                      onUpdate={handleOnPreviewUpdate}
                                    />
                                  )}
                                  <StickyNavBarOffsetProvider offset={bannerOffset}>
                                    <div id={GUEST_SITE_START_PORTAL_ID} />
                                    <GuestSiteLayout
                                      setLoadingTheme={setLoadingTheme}
                                      eventHandle={eventHandle}
                                      event={event}
                                      layout={layout}
                                      setIsPastFirstPage={setIsPastFirstPage}
                                      isNativeAccommodationPage={isNativeAccommodationPage}
                                      isNativeBrannanOverridePage={isNativeBrannanOverridePage}
                                      hideNav={shouldHideNav}
                                    />
                                  </StickyNavBarOffsetProvider>
                                </PhotoGalleryLayoutOverrideProvider>
                              </AccommodationsLayoutOverrideProvider>
                            </ScrollToProvider>
                          )}
                        </RichTextStylesProvider>
                      </EventDesignJoyKitThemeOverridesProvider>
                      <GuestSiteDebugger event={event} />
                    </ColorPaletteProvider>
                    {shouldRenderActionBar && !shouldRenderFullPageGate && <ActionBar eventId={event.id} />}
                  </GuestIdentificationDialogProvider>
                </UnlockDialogProvider>
              </EventDesignThemingProvider>
            </LayoutProvider>
          ) : null}
        </GuestSiteFeatureSet>
      </Box>
      <SmartAppBanner telemetryCategory="wedding" />
      {/* Defer rendering the purchase confirmation dialog because it imports from registry app */}
      {shouldRenderPurchaseConfirmationDialog && (
        <LazyPurchaseConfirmationDialog
          isOpen={isPurchaseConfirmationDialogOpen}
          onClose={onPurchaseConfirmationClose}
        />
      )}
    </PageContentVisibilityProvider>
  );
});

const GuestSiteBody: React.FC<GuestSiteBodyProps> = props => {
  return (
    <DiscountsProvider>
      <HotelsDiscountsProvider>
        <SmartAppBannerProvider>
          <WebsiteBannerProvider>
            <TemplatePreviewProvider>
              <GuestSiteBodyBase {...props} />
            </TemplatePreviewProvider>
          </WebsiteBannerProvider>
        </SmartAppBannerProvider>
      </HotelsDiscountsProvider>
    </DiscountsProvider>
  );
};

GuestSiteBody.displayName = 'GuestSiteBody';

export default GuestSiteBody;
